import React, { useState, useEffect } from 'react';

export const CredentialDebugger: React.FC = () => {
  const [credentials, setCredentials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastAction, setLastAction] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`${timestamp}: ${message}`, ...prev].slice(0, 20));
  };

  const fetchCredentials = async () => {
    setLoading(true);
    addLog('🔍 Fetching credentials from D1...');

    try {
      const response = await fetch('http://localhost:8787/api/aws/credentials');
      const data = await response.json();

      addLog(`📥 Response received: ${JSON.stringify(data).substring(0, 100)}...`);

      if (data.success && data.credentials) {
        setCredentials(data.credentials);
        addLog(`✅ Found ${data.credentials.length} saved credentials in D1`);

        if (data.credentials.length > 0) {
          data.credentials.forEach((cred: any) => {
            addLog(`  - ID: ${cred.id}, Key: ${cred.access_key_id?.substring(0, 8)}..., Created: ${cred.created_at}`);
          });
        }
      } else {
        addLog('❌ No credentials found or error in response');
      }
    } catch (error: any) {
      addLog(`❌ Error fetching: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteCredential = async (id: number) => {
    addLog(`🗑️ Attempting to delete credential ID: ${id}`);

    try {
      const response = await fetch(`http://localhost:8787/api/aws/credentials/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        addLog(`✅ Deleted credential ID: ${id}`);
        fetchCredentials();
      } else {
        addLog(`❌ Failed to delete: ${data.error}`);
      }
    } catch (error: any) {
      addLog(`❌ Delete error: ${error.message}`);
    }
  };

  const testSaveCredential = async () => {
    addLog('🚀 Testing credential save...');

    const testData = {
      accessKeyId: 'AKIATEST' + Math.random().toString(36).substring(2, 14).toUpperCase(),
      secretAccessKey: 'test' + Math.random().toString(36).substring(2, 38),
      region: 'us-east-1',
      userName: 'test-user-' + Date.now(),
      credentialType: 'iam',
      userId: 1
    };

    addLog(`📤 Sending: ${JSON.stringify(testData).substring(0, 100)}...`);

    try {
      const response = await fetch('http://localhost:8787/api/aws/credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testData)
      });

      const data = await response.json();
      addLog(`📥 Save response: ${JSON.stringify(data)}`);

      if (data.success) {
        addLog(`✅ Saved with ID: ${data.credentialId}`);
        fetchCredentials();
      } else {
        addLog(`❌ Save failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      addLog(`❌ Save error: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  return (
    <div className="fixed bottom-4 right-4 w-96 max-h-96 bg-black border-2 border-green-500 rounded-lg p-4 overflow-hidden z-50">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-green-400 font-bold">🔧 D1 Credential Debugger</h3>
        <button
          onClick={() => document.getElementById('debugger')?.remove()}
          className="text-red-500 hover:text-red-400"
        >
          ✕
        </button>
      </div>

      {/* Current State */}
      <div className="bg-gray-900 rounded p-2 mb-2">
        <div className="text-xs text-gray-400">
          <div>📊 Total Saved: <span className="text-white font-bold">{credentials.length}</span></div>
          <div>🔄 Loading: <span className={loading ? 'text-yellow-400' : 'text-gray-600'}>{loading ? 'YES' : 'NO'}</span></div>
          <div>⏰ Last Check: {new Date().toLocaleTimeString()}</div>
        </div>
      </div>

      {/* Saved Credentials */}
      <div className="bg-gray-900 rounded p-2 mb-2 max-h-24 overflow-y-auto">
        <div className="text-xs font-bold text-gray-400 mb-1">Saved in D1:</div>
        {credentials.length === 0 ? (
          <div className="text-xs text-red-400">⚠️ No credentials in database!</div>
        ) : (
          credentials.map((cred: any) => (
            <div key={cred.id} className="text-xs text-green-400 flex justify-between items-center">
              <span>
                #{cred.id}: {cred.access_key_id?.substring(0, 10)}...
                ({cred.user_name || 'no-name'})
              </span>
              <button
                onClick={() => deleteCredential(cred.id)}
                className="text-red-400 hover:text-red-300"
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mb-2">
        <button
          onClick={fetchCredentials}
          className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded text-xs"
        >
          🔄 Refresh
        </button>
        <button
          onClick={testSaveCredential}
          className="flex-1 px-2 py-1 bg-green-600 hover:bg-green-500 rounded text-xs"
        >
          💾 Test Save
        </button>
      </div>

      {/* Activity Log */}
      <div className="bg-black rounded p-2 max-h-32 overflow-y-auto">
        <div className="text-xs font-bold text-gray-400 mb-1">Activity Log:</div>
        <div className="space-y-1">
          {logs.map((log, idx) => (
            <div key={idx} className="text-xs text-gray-300 font-mono">
              {log}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};